import { memoize } from '@exodus/basic-utils';
import EventEmitter from 'eventemitter3';
import createFetchival from '@exodus/fetch/create-fetchival';
import { SearchDirection } from './types.js';
import getFiatBalance from '../utils/get-fiat-balance.js';
const MEMOIZATION_TIMEOUT = 1000 * 60 * 5;
export class ExchangeAssets extends EventEmitter {
    #requestAssets;
    #config;
    #assetsModule;
    #exchangeProviders;
    #enabledAssetsAtom;
    #fiatBalancesAtom;
    #availableAssetNamesByWalletAccountAtom;
    #activeWalletAccountAtom;
    constructor(params) {
        super();
        this.#assetsModule = params.assetsModule;
        this.#config = params.config;
        this.#exchangeProviders = params.exchangeProviders;
        this.#enabledAssetsAtom = params.enabledAssetsAtom;
        this.#fiatBalancesAtom = params.fiatBalancesAtom;
        this.#availableAssetNamesByWalletAccountAtom = params.availableAssetNamesByWalletAccountAtom;
        this.#activeWalletAccountAtom = params.activeWalletAccountAtom;
        const fetchival = createFetchival({ fetch: params.fetch });
        this.#requestAssets = fetchival(new URL(`${this.#config.services.assets.baseUrl}/assets`));
    }
    _getAvailableAssetIds = async (walletAccount) => {
        const _walletAccount = walletAccount || (await this.#activeWalletAccountAtom.get());
        const availableAssetNamesByWalletAccount = await this.#availableAssetNamesByWalletAccountAtom.get();
        const availableAssetNames = availableAssetNamesByWalletAccount[_walletAccount];
        if (!availableAssetNames) {
            return new Set();
        }
        const availableAssetIds = new Set();
        for (const asset of Object.values(this.#assetsModule.getAssets())) {
            if (availableAssetNames.includes(asset.name)) {
                availableAssetIds.add(asset.ticker);
            }
        }
        return availableAssetIds;
    };
    getSupportedFromAssetIds = memoize(async (walletAccount) => {
        const [supportedFromAssetIds, availableAssetIds] = await Promise.all([
            this.#exchangeProviders.getSupportedFromAssetIds(),
            this._getAvailableAssetIds(walletAccount),
        ]);
        return [...supportedFromAssetIds].filter((assetId) => availableAssetIds.has(assetId));
    }, (walletAccount) => walletAccount || '', MEMOIZATION_TIMEOUT);
    getSupportedToAssetIds = memoize(async (fromAssetId, walletAccount) => {
        const [supportedToAssetIds, availableAssetIds] = await Promise.all([
            this.#exchangeProviders.getSupportedToAssetIds(fromAssetId),
            this._getAvailableAssetIds(walletAccount),
        ]);
        return [...supportedToAssetIds].filter((assetId) => availableAssetIds.has(assetId));
    }, (fromAssetId, walletAccount) => `getSupportedToAssetIds-${fromAssetId}-${walletAccount || ''}`, MEMOIZATION_TIMEOUT);
    searchAssets = memoize(async ({ query, walletAccount, direction = SearchDirection.To, }) => {
        const tokens = await this.#requestAssets.get({
            query,
        });
        if (tokens.length === 0) {
            this.emit('empty-search', { query, direction, walletAccount });
            return [];
        }
        const [enabledAssetNames, availableAssetNamesByWalletAccount, fiatBalances] = await Promise.all([
            this.#enabledAssetsAtom.get(),
            this.#availableAssetNamesByWalletAccountAtom.get(),
            this.#fiatBalancesAtom.get(),
        ]);
        const availableAssetNamesForWalletAccount = new Set(availableAssetNamesByWalletAccount[walletAccount] || []);
        const enabledAssets = Object.values(this.#assetsModule.getAssets()).filter((asset) => enabledAssetNames[asset.name]);
        const enabledAssetByTicker = new Map(enabledAssets.map((asset) => [asset.ticker, asset]));
        const isBaseAssetSupported = (token) => Boolean(availableAssetNamesForWalletAccount.has(token.network));
        const availableTokens = tokens.filter((token) => isBaseAssetSupported(token));
        return availableTokens.sort((a, b) => {
            const isAEnabled = enabledAssetByTicker.get(a.id);
            const isBEnabled = enabledAssetByTicker.get(b.id);
            if (isAEnabled && !isBEnabled)
                return -1;
            if (!isAEnabled && isBEnabled)
                return 1;
            if (isAEnabled && isBEnabled) {
                const aAsset = this.getAsset({ assetId: a.id });
                const bAsset = this.getAsset({ assetId: b.id });
                const aFiatBalance = getFiatBalance({
                    assetName: aAsset.name,
                    fiatBalances,
                    walletAccount,
                });
                const bFiatBalance = getFiatBalance({
                    assetName: bAsset.name,
                    fiatBalances,
                    walletAccount,
                });
                return bFiatBalance.sub(aFiatBalance).toDefaultNumber();
            }
            return 0;
        });
    }, ({ query, walletAccount, direction }) => `${query}-${walletAccount}-${direction}`, MEMOIZATION_TIMEOUT);
    getAsset = memoize((params) => {
        const asset = 'assetName' in params
            ? this.#assetsModule.getAsset(params.assetName)
            : Object.values(this.#assetsModule.getAssets()).find((asset) => asset.ticker === params.assetId);
        if (!asset) {
            throw new Error(`Unknown asset for ${'assetName' in params ? params.assetName : params.assetId}`);
        }
        return asset;
    }, (params) => ('assetName' in params ? params.assetName : params.assetId), MEMOIZATION_TIMEOUT);
    isAssetExchangeableFrom = memoize(async (assetId, walletAccount) => {
        const [isExchangeableFrom, availableAssetIds] = await Promise.all([
            this.#exchangeProviders.isAssetExchangeableFrom(assetId),
            this._getAvailableAssetIds(walletAccount),
        ]);
        return isExchangeableFrom && availableAssetIds.has(assetId);
    }, (assetId, walletAccount) => `${assetId}_${walletAccount || ''}`, MEMOIZATION_TIMEOUT);
    isAssetExchangeableTo = memoize(async (assetId, walletAccount) => {
        const [isAssetExchangeableTo, availableAssetIds] = await Promise.all([
            this.#exchangeProviders.isAssetExchangeableTo(assetId),
            this._getAvailableAssetIds(walletAccount),
        ]);
        return isAssetExchangeableTo && availableAssetIds.has(assetId);
    }, (assetId, walletAccount) => `${assetId}_${walletAccount || ''}`, MEMOIZATION_TIMEOUT);
    isAssetExchangeable = memoize(async (assetId, walletAccount) => {
        const [isAssetExchangeable, availableAssetIds] = await Promise.all([
            this.#exchangeProviders.isAssetExchangeable(assetId),
            this._getAvailableAssetIds(walletAccount),
        ]);
        return isAssetExchangeable && availableAssetIds.has(assetId);
    }, (assetId, walletAccount) => `${assetId}_${walletAccount || ''}`, MEMOIZATION_TIMEOUT);
    isPairSupported = memoize(async (pairId, walletAccount) => {
        const [from, to] = pairId.split('_');
        const [supportedProviders, availableAssetIds] = await Promise.all([
            this.#exchangeProviders.getSupportedProvidersForPair(pairId),
            this._getAvailableAssetIds(walletAccount),
        ]);
        return (supportedProviders.length > 0 && availableAssetIds.has(from) && availableAssetIds.has(to));
    }, (pairId, walletAccount) => `${pairId}_${walletAccount || ''}`, MEMOIZATION_TIMEOUT);
}
const exchangeAssetsModuleFactory = (args) => new ExchangeAssets({ ...args });
const exchangeAssetsModuleDefinition = {
    id: 'exchangeAssets',
    type: 'module',
    factory: exchangeAssetsModuleFactory,
    dependencies: [
        'config',
        'fetch',
        'assetsModule',
        'exchangeProviders',
        'enabledAssetsAtom',
        'availableAssetNamesByWalletAccountAtom',
        'fiatBalancesAtom',
        'activeWalletAccountAtom',
    ],
};
export default exchangeAssetsModuleDefinition;
